// packages/shared/src/schedule.ts
// Schedule preset <-> cron conversion + next-run + human summary.
//
// Locked contract for Phase 5 cron evaluator reuse — keep this signature stable.
// All cron parsing uses tz: BASTN_TIMEZONE (America/New_York) per RESEARCH
// Pitfall 3 — without it, cron-parser fires 4-5 hours early during EDT.
//
// Public surface (consumed by 03-03 ScheduleEditor + ReminderTable + ReminderEditorForm):
//   SchedulePreset            — discriminated union of supported preset shapes
//   presetToCron(p)           — preset -> cron string ('0 9 * * *') or null for manual
//   cronToPreset(cron)        — cron string -> preset OR { type: 'custom', cron }
//   nextRunFromCron(cron)     — next fire time as Date (or null for manual)
//   nextRunRelative(cron)     — 'in 14 hours' / 'Manual' (date-fns formatDistance)
//   scheduleSummary(cron)     — 'Daily at 09:00 ET' / cronstrue fallback / 'Manual'

import cronParser from 'cron-parser';
import cronstrue from 'cronstrue';
import { formatDistance } from 'date-fns';
import { BASTN_TIMEZONE } from './constants';

// cron-parser 4.x exposes parseExpression (CommonJS default export shape) —
// 5.x renamed this to CronExpressionParser.parse. We pin to 4.x's API here.
// Either form accepts { currentDate, tz } and returns an iterator; .next().toDate()
// returns a JS Date in UTC representing the next firing instant in the given tz.

export type SchedulePreset =
  | { type: 'manual' }
  | { type: 'daily'; hhmm: string }
  | { type: 'weekly'; dayOfWeek: 0 | 1 | 2 | 3 | 4 | 5 | 6; hhmm: string }
  | { type: 'event'; eventId?: string; eventMatchMode: 'specific' | 'all'; leadMinutes: number };

export type ScheduleResolution =
  | SchedulePreset
  | { type: 'custom'; cron: string };

const HHMM_RE = /^([01]\d|2[0-3]):[0-5]\d$/;

/**
 * Convert a SchedulePreset into a 5-field cron string ('m h dom mon dow') or null.
 * Manual and event presets return null (NOT a cron string — the dispatcher must skip these).
 */
export function presetToCron(p: SchedulePreset): string | null {
  if (p.type === 'manual') return null;
  if (p.type === 'event') return null; // Event-triggered reminders have no cron schedule
  if (!HHMM_RE.test(p.hhmm)) {
    throw new Error(`presetToCron: invalid hhmm '${p.hhmm}'`);
  }
  const [hStr, mStr] = p.hhmm.split(':');
  const h = Number(hStr);
  const m = Number(mStr);
  if (p.type === 'daily') return `${m} ${h} * * *`;
  return `${m} ${h} * * ${p.dayOfWeek}`;
}

/**
 * Extract event-trigger wire fields from an event preset.
 * Returns null for non-event presets.
 * Used by the Pattern 7 boundary translator in ReminderEditorForm.onSubmit.
 */
export function eventTriggerFromPreset(
  preset: SchedulePreset,
): { event_id: string | null; event_match_mode: 'specific' | 'all'; event_lead_minutes: number } | null {
  if (preset.type !== 'event') return null;
  return {
    event_id:           preset.eventId ?? null,
    event_match_mode:   preset.eventMatchMode,
    event_lead_minutes: preset.leadMinutes,
  };
}

/**
 * Converts a Reminder row back to a SchedulePreset, including event-trigger variants.
 * When the reminder has triggerType='event' AND eventMatchMode is non-null,
 * returns the event preset shape. Otherwise falls through to cronToPreset.
 */
export function cronToPresetWithEvent(reminder: {
  scheduleCron?: string | null;
  triggerType?: string | null;
  eventId?: string | null;
  eventMatchMode?: string | null;
  eventLeadMinutes?: number | null;
}): SchedulePreset | { type: 'custom'; cron: string } {
  if (
    reminder.triggerType === 'event' &&
    reminder.eventMatchMode != null
  ) {
    return {
      type:           'event',
      eventId:        reminder.eventId ?? undefined,
      eventMatchMode: reminder.eventMatchMode as 'specific' | 'all',
      leadMinutes:    reminder.eventLeadMinutes ?? 30,
    };
  }
  return cronToPreset(reminder.scheduleCron ?? null);
}

/**
 * Reverse of presetToCron. Recognises the locked Daily and Weekly templates
 * exactly; anything else (intervals, ranges, multi-DOW) is returned as
 * `{ type: 'custom', cron }` so the SPA can render the literal expression.
 */
export function cronToPreset(cron: string | null): ScheduleResolution {
  if (cron === null) return { type: 'manual' };
  const parts = cron.trim().split(/\s+/);
  if (parts.length !== 5) return { type: 'custom', cron };
  const [m, h, dom, mon, dow] = parts;
  const minutes = Number(m);
  const hours = Number(h);
  if (!Number.isInteger(minutes) || !Number.isInteger(hours)) {
    return { type: 'custom', cron };
  }
  if (minutes < 0 || minutes > 59 || hours < 0 || hours > 23) {
    return { type: 'custom', cron };
  }
  const hhmm = `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
  if (dom === '*' && mon === '*' && dow === '*') {
    return { type: 'daily', hhmm };
  }
  if (dom === '*' && mon === '*' && /^[0-6]$/.test(dow)) {
    return {
      type: 'weekly',
      dayOfWeek: Number(dow) as 0 | 1 | 2 | 3 | 4 | 5 | 6,
      hhmm,
    };
  }
  return { type: 'custom', cron };
}

/**
 * Resolve the next fire time of a cron expression in BASTN_TIMEZONE.
 * Returns null for manual (cron === null) and on parser errors.
 */
export function nextRunFromCron(
  cron: string | null,
  now: Date = new Date(),
): Date | null {
  if (cron === null) return null;
  try {
    const it = cronParser.parseExpression(cron, {
      currentDate: now,
      tz: BASTN_TIMEZONE,
    });
    return it.next().toDate();
  } catch {
    return null;
  }
}

/**
 * Human-readable "in 3d 4h" / "Manual" relative string. Uses date-fns
 * formatDistance against `now` (defaults to new Date()).
 * For event presets, returns a descriptive label (caller provides event name if known).
 */
export function nextRunRelative(
  cron: string | null,
  now: Date = new Date(),
): string {
  const next = nextRunFromCron(cron, now);
  if (next === null) return 'Manual';
  return `in ${formatDistance(next, now)}`;
}

/**
 * Format a millisecond offset into a compact relative string for the LiveCountdown.
 * Thresholds (per must_haves truth #1):
 *   - <= 0:         'now'
 *   - <= 30_000:    'firing soon…'
 *   - <= 60_000:    'in <1m'
 *   - <= 3_600_000: 'in Nm'
 *   - <= 86_400_000: 'in Nh'
 *   - > 86_400_000: 'in Nd'
 */
export function formatCountdownMs(ms: number): string {
  if (ms <= 0) return 'now';
  if (ms <= 30_000) return 'firing soon…';
  if (ms <= 60_000) return 'in <1m';
  if (ms <= 3_600_000) return `in ${Math.round(ms / 60_000)}m`;
  if (ms <= 86_400_000) return `in ${Math.round(ms / 3_600_000)}h`;
  return `in ${Math.round(ms / 86_400_000)}d`;
}

/**
 * Human-readable schedule summary. Prefers the preset shape ('Daily at 09:00 ET');
 * falls back to cronstrue for custom expressions; returns 'Manual' for null cron.
 *
 * The 'ET' suffix is locked — every BASTN reminder evaluates against
 * BASTN_TIMEZONE (America/New_York), so all preset-shape summaries display ET.
 */
export function scheduleSummary(cron: string | null): string {
  if (cron === null) return 'Manual';
  const preset = cronToPreset(cron);
  if (preset.type === 'daily') return `Daily at ${preset.hhmm} ET`;
  if (preset.type === 'weekly') {
    const days = [
      'Sunday',
      'Monday',
      'Tuesday',
      'Wednesday',
      'Thursday',
      'Friday',
      'Saturday',
    ];
    return `Weekly on ${days[preset.dayOfWeek]} at ${preset.hhmm} ET`;
  }
  try {
    return cronstrue.toString(cron);
  } catch {
    return cron;
  }
}
